import React from "react";
import { cn } from "@/lib/utils";
import { StatusBadge } from "@/components/ui/StatusBadge";
import { Button } from "@/components/ui/Button";
import { RefreshCw, ArrowRight, Clock, Layers, Clock3 } from "lucide-react";
import { formatTimestamp } from "@/lib/catalog/formatTimestamp";
import type { MarketplaceCardProps } from "./MarketplaceCard.types";

const connectionStatusMap: Record<string, string> = {
  HEALTHY: "ACTIVE",
  DEGRADED: "WARNING",
  DISCONNECTED: "FAILED",
};

const syncStatusMap: Record<string, { badge: string; label: string }> = {
  SYNCED: { badge: "ACTIVE", label: "Synced" },
  SYNCING: { badge: "PENDING", label: "Syncing" },
  NEVER_SYNCED: { badge: "DISABLED", label: "Not Synced" },
  FAILED: { badge: "FAILED", label: "Failed" },
};

// Marketplaces where users can initiate sync actions
function canSync(status: string): boolean {
  return status === "ACTIVE" || status === "COMING_SOON";
}

export const MarketplaceCard: React.FC<MarketplaceCardProps> = ({
  marketplace,
  onSync,
  onGoToSync,
  selected = false,
  onClick,
  compact = false,
  className,
}) => {
  const m = marketplace as Record<string, unknown>;

  if (compact) {
    return (
      <div
        data-testid={`marketplace-card-${m.id}`}
        onClick={onClick}
        className={cn(
          "p-3 rounded-lg border cursor-pointer transition-all duration-150",
          selected
            ? "bg-indigo-50 dark:bg-indigo-950 border-indigo-300 dark:border-indigo-700"
            : "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600",
          className,
        )}
      >
        <div className="mb-2">
          {m.logo ? (
            <img
              src={m.logo as string}
              alt={String(m.name ?? "")}
              className="w-8 h-8 object-contain"
            />
          ) : (
            <span className="w-8 h-8 rounded bg-slate-200 dark:bg-slate-700 block" />
          )}
        </div>
        <div className="mb-1.5">
          {(() => {
            const s = syncStatusMap[String(m.syncStatus)] ?? {
              badge: "DISABLED",
              label: "Not Synced",
            };
            return (
              <StatusBadge status={s.badge} showDot={false}>
                {s.label}
              </StatusBadge>
            );
          })()}
        </div>
        <div className="flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400">
          <Clock className="w-3 h-3" />
          {formatTimestamp((m.lastSyncedAt ?? m.lastSync) as string | null)}
        </div>
      </div>
    );
  }

  const status = String(m.status ?? "ACTIVE");
  const syncable = canSync(status);
  const isInactive = status === "INACTIVE";
  const isComingSoon = status === "COMING_SOON";

  return (
    <div
      data-testid={`marketplace-card-${m.id}`}
      className={cn(
        "p-5 rounded-lg border transition-shadow duration-200",
        isInactive
          ? "bg-transparent border-slate-200 dark:border-slate-800"
          : "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 hover:shadow-md",
        className,
      )}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          {m.logo ? (
            <img
              src={m.logo as string}
              alt={String(m.name ?? "")}
              className={cn(
                "w-8 h-8 object-contain",
                isInactive && "opacity-40",
              )}
            />
          ) : (
            <span
              className={cn(
                "w-8 h-8 rounded bg-slate-200 dark:bg-slate-700",
                isInactive && "opacity-40",
              )}
            />
          )}
          <div>
            <h3
              className={cn(
                "font-semibold",
                isInactive
                  ? "text-slate-400 dark:text-slate-500"
                  : "text-slate-900 dark:text-slate-100",
              )}
            >
              {String(m.name ?? "")}
            </h3>
            <div className="flex items-center gap-2 mt-1">
              {isComingSoon ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-violet-50 dark:bg-violet-950 text-violet-600 dark:text-violet-400 border border-violet-200 dark:border-violet-800">
                  <Clock3 className="w-3 h-3" />
                  Coming Soon
                </span>
              ) : isInactive ? (
                <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-slate-700">
                  Inactive
                </span>
              ) : (
                <StatusBadge
                  status={
                    connectionStatusMap[String(m.connectionStatus)] ?? "ACTIVE"
                  }
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {!isInactive && (
        <>
          <div className="flex items-center justify-between py-3 border-t border-slate-100 dark:border-slate-800">
            <div>
              <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">
                Catalog Sync
              </p>
              {(() => {
                const s = syncStatusMap[String(m.syncStatus)] ?? {
                  badge: "DISABLED",
                  label: "Not Synced",
                };
                return <StatusBadge status={s.badge}>{s.label}</StatusBadge>;
              })()}
            </div>
            <div className="text-right">
              <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">
                Last Sync
              </p>
              <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                {formatTimestamp(
                  (m.lastSyncedAt ?? m.lastSync) as string | null,
                )}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-1.5 py-2 text-xs text-indigo-600 dark:text-indigo-400">
            <Layers className="w-3.5 h-3.5" />
            Synced with Canonical Model
          </div>
        </>
      )}

      {isInactive && (
        <p className="text-xs text-slate-400 dark:text-slate-500 py-3 border-t border-slate-100 dark:border-slate-800">
          This marketplace is currently inactive and not available for sync.
        </p>
      )}

      {m.stats && typeof m.stats === "object" && !isInactive && (
        <div className="grid grid-cols-3 gap-4 py-3 border-t border-slate-100 dark:border-slate-800">
          <div>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Products
            </p>
            <p className="text-lg font-semibold text-slate-900 dark:text-slate-100">
              {Number(
                (m.stats as Record<string, unknown>).products ?? 0,
              ).toLocaleString()}
            </p>
          </div>
          <div>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Categories
            </p>
            <p className="text-lg font-semibold text-slate-900 dark:text-slate-100">
              {Number(
                (m.stats as Record<string, unknown>).categories ?? 0,
              ).toLocaleString()}
            </p>
          </div>
          <div>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Attributes
            </p>
            <p className="text-lg font-semibold text-slate-900 dark:text-slate-100">
              {Number(
                (m.stats as Record<string, unknown>).attributes ?? 0,
              ).toLocaleString()}
            </p>
          </div>
        </div>
      )}

      <div className="flex items-center gap-2 pt-3 border-t border-slate-100 dark:border-slate-800">
        {syncable ? (
          <>
            <Button
              data-testid={`sync-${m.id}`}
              variant="secondary"
              size="sm"
              onClick={() => onSync?.(marketplace)}
              className="flex-1"
            >
              <RefreshCw className="w-4 h-4" />
              {m.syncStatus === "SYNCED" ? "Re-sync" : "Start Sync"}
            </Button>
            <Button
              data-testid={`goto-sync-${m.id}`}
              variant="ghost"
              size="sm"
              onClick={() => onGoToSync?.(marketplace)}
            >
              <ArrowRight className="w-4 h-4" />
            </Button>
          </>
        ) : (
          <p className="text-xs text-slate-400 dark:text-slate-500 w-full text-center py-1">
            No actions available
          </p>
        )}
      </div>
    </div>
  );
};
